import { createTheme } from "@mui/material/styles";
import "@fontsource/roboto";

export const createAppTheme = (colorSchemes) =>
  createTheme({
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            displayLarge: "h1",
            displayMedium: "h2",
            displaySmall: "h3",
            headlineLarge: "h4",
            headlineMedium: "h5",
            headlineSmall: "h6",
            titleLarge: "p",
            titleMedium: "p",
            titleSmall: "p",
            labelLarge: "span",
            labelMedium: "span",
            labelSmall: "span",
            bodyLarge: "p",
            bodyMedium: "p",
            bodySmall: "p",
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            "&:last-child": {
              paddingBottom: "0",
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { variant: "contained" },
                style: {},
              },
              {
                props: { variant: "outlined" },
                style: {
                  backgroundColor: "transparent",
                  border: "1px solid",
                },
              },
            ],
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            transition: "all 0.3s ease-out",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "16px",
          },
        },
        variants: [
          {
            props: { variant: "contained-round" },
            style: {
              borderRadius: "100px",
            },
          },
          {
            props: { variant: "outlined-round" },
            style: {
              borderRadius: "100px",
              backgroundColor: "transparent",
              border: "1px solid",
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              backgroundColor: "transparent",
              border: "1px solid",
            },
          },
          {
            props: { size: "large" },
            style: {
              padding: "10px 24px",
            },
          },
        ],
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            "& .MuiTabs-indicator": {
              borderRadius: "100% 100% 0% 0%",
              height: "3px",
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["box-shadow", "background-color", "color", "border-color"],
              {
                duration: theme.transitions.duration.standard,
                easing: theme.transitions.easing.easeInOut,
              }
            ),
          }),
        },
      },
    },

    typography: {
      fontFamily: "Roboto, Arial, sans-serif",
      displayLarge: {
        fontSize: "57px",
        lineHeight: "64px",
        letterSpacing: "-0.25px",
        fontWeight: 400,
      },
      displayMedium: {
        fontSize: "45px",
        lineHeight: "52px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      displaySmall: {
        fontSize: "36px",
        lineHeight: "44px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      headlineLarge: {
        fontSize: "32px",
        lineHeight: "40px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      headlineMedium: {
        fontSize: "28px",
        lineHeight: "36px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      headlineSmall: {
        fontSize: "24px",
        lineHeight: "32px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      titleLarge: {
        fontSize: "22px",
        lineHeight: "28px",
        letterSpacing: "0px",
        fontWeight: 400,
      },
      titleMedium: {
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "+0.15px",
        fontWeight: 500,
      },
      titleSmall: {
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "+0.1px",
        fontWeight: 500,
      },
      labelLarge: {
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "+0.1px",
        fontWeight: 500,
      },
      labelMedium: {
        fontSize: "12px",
        lineHeight: "16px",
        letterSpacing: "+0.5px",
        fontWeight: 500,
      },
      labelSmall: {
        fontSize: "11px",
        lineHeight: "16px",
        letterSpacing: "+0.5px",
        fontWeight: 500,
      },
      bodyLarge: {
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "+0.5px",
        fontWeight: 400,
      },
      bodyMedium: {
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "+0.25px",
        fontWeight: 400,
      },
      bodySmall: {
        fontSize: "12px",
        lineHeight: "16px",
        letterSpacing: "+0.4px",
        fontWeight: 400,
      },
    },

    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },

    colorSchemes,
  });

export default createAppTheme;
